import { ChakraFlex, FAIconName, Icon, Tag, Text } from '@equip.health/ui';
import { DateTime } from 'luxon';
import { CarePlanNoteStatusHistoryResponse } from '~/lib/types/carePlan/carePlans';
import ProviderAvatarBlockTooltip from '~/components/common/ProviderAvatarBlockTooltip';
import { getTagColorByCarePlanGoalStatusCode } from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalTagDropdownOptions';

export type PatientNoteGoalStatusHistoryTooltipContentProps = {
  providerExternalId: string;
  goalId: string;
  statusHistory?: CarePlanNoteStatusHistoryResponse;
  description?: string | null;
};

const PatientNoteGoalStatusHistoryTooltipContent = ({
  providerExternalId,
  goalId,
  statusHistory,
  description,
}: PatientNoteGoalStatusHistoryTooltipContentProps) => {
  const renderStatusDisplay = () => {
    if (!statusHistory) return null;

    const shouldShowSingleTag =
      statusHistory.statusFrom.code === statusHistory.statusTo.code;

    if (shouldShowSingleTag) {
      return (
        <ChakraFlex alignItems="center" gap="8px">
          <Tag
            color={getTagColorByCarePlanGoalStatusCode(
              statusHistory.statusTo.code,
            )}
            id={`goal-status-history-to-${statusHistory.id}`}
            value={statusHistory.statusTo.label}
          />
        </ChakraFlex>
      );
    }

    return (
      <ChakraFlex alignItems="center" gap="8px">
        <Tag
          color={getTagColorByCarePlanGoalStatusCode(
            statusHistory.statusFrom.code,
          )}
          id={`goal-status-history-from-${statusHistory.id}`}
          value={statusHistory.statusFrom.label}
        />
        <Icon
          aria-hidden={false}
          aria-label="Changed to"
          color="accent.blue"
          name={FAIconName.arrowRightRegular}
        />
        <Tag
          color={getTagColorByCarePlanGoalStatusCode(
            statusHistory.statusTo.code,
          )}
          id={`goal-status-history-to-${statusHistory.id}`}
          value={statusHistory.statusTo.label}
        />
      </ChakraFlex>
    );
  };

  return (
    <ChakraFlex direction="column" gap="12px">
      <ProviderAvatarBlockTooltip
        isRoleAtAction={false}
        providerExternalId={providerExternalId}
        shouldShowDisplayName={true}
        TOP_LEVEL_ID={`goal-status-provider-avatar-tooltip-${goalId}`}
      />

      {statusHistory?.effectiveDate && (
        <Text variant="caption" color="neutral.secondary">
          Last updated{' '}
          {DateTime.fromISO(statusHistory.effectiveDate).toFormat(
            "MMM d, yyyy 'at' h:mm a",
          )}
        </Text>
      )}
      {statusHistory && (
        <ChakraFlex direction="column" gap="12px">
          {renderStatusDisplay()}
          {description && (
            <ChakraFlex
              backgroundColor="secondary.5"
              borderRadius="12px"
              padding="12px"
            >
              <Text variant="caption">{description}</Text>
            </ChakraFlex>
          )}
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

export default PatientNoteGoalStatusHistoryTooltipContent;
